import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, MessageCircle, CheckCircle, AlertTriangle, ChevronDown, RefreshCw } from 'lucide-react';
import { useAuth, getPortfolios, getMessages, getLatestApproval, syncFromSupabase, getSettings } from '../context/AuthContext';
import { isSupabaseConfigured } from '../lib/supabase';
import { BENCHMARK_META } from '../lib/mockData';
import { isConfigured as isFinnhubConfigured, getRealPerformanceReturns, clearMarketCaches, getQuote } from '../lib/finnhub';
import { isConfigured as isSchwabConfigured, getSchwabPositions } from '../lib/schwab';
import PerformanceChart from '../components/PerformanceChart';
import HoldingsPerformanceChart from '../components/HoldingsPerformanceChart';
import AllocationPieChart from '../components/AllocationPieChart';
import MessagePanel from '../components/MessagePanel';
import TickerSummaryModal from '../components/TickerSummaryModal';

export default function ClientPortal() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [summaryTicker, setSummaryTicker] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [realReturns, setRealReturns] = useState(null);
  const [liveQuotes, setLiveQuotes] = useState({});
  const [schwabData, setSchwabData] = useState(null);  // { totalValue, positions }
  const schwabValue = schwabData?.totalValue ?? null;

  const handleRefresh = useCallback(async () => {
    if (isSupabaseConfigured && user) {
      await syncFromSupabase(user.id);
    }
    clearMarketCaches();
    setRefreshKey((k) => k + 1);
    if (user) setPortfolios(getPortfolios(user.id));
  }, [user]);

  useEffect(() => {
    if (user) {
      const all = getPortfolios(user.id);
      setPortfolios(all);
    }
  }, [user]);

  const portfolio = useMemo(() => portfolios[selectedIdx] || null, [portfolios, selectedIdx]);
  const holdings = useMemo(() => portfolio?.holdings ?? [], [portfolio]);
  const benchmark = portfolio?.primary_benchmark;
  const approval = useMemo(() => portfolio ? getLatestApproval(portfolio.id) : null, [portfolio]);
  const userName = useMemo(() => user ? (getSettings(user.id).display_name || '') : '', [user]);

  // Fetch real performance returns (same source as advisor)
  useEffect(() => {
    if (!holdings.length || !isFinnhubConfigured()) return;
    let cancelled = false;
    getRealPerformanceReturns(holdings, benchmark || null)
      .then((data) => { if (!cancelled && data) setRealReturns(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [holdings, benchmark, refreshKey]);

  // Fetch live quotes for dynamic portfolio value
  useEffect(() => {
    if (!holdings.length || !isFinnhubConfigured()) return;
    let cancelled = false;
    Promise.all(
      holdings.map((h) =>
        getQuote(h.ticker).then((q) => [h.ticker, q]).catch(() => [h.ticker, null])
      )
    ).then((results) => {
      if (cancelled) return;
      const quotes = {};
      for (const [ticker, q] of results) {
        if (q?.c) quotes[ticker] = q.c;
      }
      setLiveQuotes(quotes);
    });
    return () => { cancelled = true; };
  }, [holdings, refreshKey]);

  // Fetch Schwab account value if portfolio is linked
  useEffect(() => {
    if (!isSchwabConfigured() || !user || !portfolio?.schwab_account_hash) {
      setSchwabData(null);
      return;
    }
    let cancelled = false;
    getSchwabPositions(user.id, portfolio.schwab_account_hash)
      .then(data => { if (!cancelled && data) setSchwabData(data); })
      .catch(err => { console.warn('[Schwab] ClientPortal fetch failed:', err.message); if (!cancelled) setSchwabData(null); });
    return () => { cancelled = true; };
  }, [user, portfolio?.schwab_account_hash, refreshKey]);

  // Auto-refresh every 5 minutes during market hours (with cache clear)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const day = now.getDay();
      if (day === 0 || day === 6) return;
      clearMarketCaches();
      setRefreshKey((k) => k + 1);
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!user || role !== 'client') {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
        <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-700 mb-2">Client Portal</h1>
        <p className="text-slate-500">This portal is available for invited clients. Please use an invite link from your advisor.</p>
      </div>
    );
  }

  if (!portfolios.length) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
        <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-700 mb-2">Client Portal</h1>
        <p className="text-slate-500 mb-4">No portfolios have been shared with you yet.</p>
        <p className="text-sm text-slate-400">Ask your advisor for an invite link to get started.</p>
      </div>
    );
  }

  const benchLabel = benchmark ? (BENCHMARK_META[benchmark]?.label ?? benchmark) : null;

  // Compute performance metrics — real market data only, no simulated fallbacks
  const ytdReturn = holdings.length && realReturns?.portfolio?.YTD != null
    ? Number(realReturns.portfolio.YTD) : null;
  const sinceReturn = holdings.length && realReturns?.portfolio?.['Since Inception'] != null
    ? Number(realReturns.portfolio['Since Inception']) : null;
  const oneYearReturn = holdings.length && realReturns?.portfolio?.['1Y'] != null
    ? Number(realReturns.portfolio['1Y']) : null;
  const benchYtd = benchmark && realReturns?.benchmark?.YTD != null
    ? Number(realReturns.benchmark.YTD) : null;

  // Dynamic portfolio value — use Schwab real value when available
  const currentPortfolioValue = useMemo(() => {
    if (schwabValue != null) return schwabValue;
    const startVal = Number(portfolio?.starting_value) || 0;
    if (!startVal || !holdings.length) return startVal || null;
    const cashPct = portfolio?.cash_percent ?? 0;
    const investedFrac = 1 - cashPct / 100;
    const growthFactor = holdings.reduce((s, h) => {
      const currentPrice = liveQuotes[h.ticker] || h.last_price;
      const entryPrice = h.entry_price ?? h.last_price;
      const ratio = entryPrice > 0 ? currentPrice / entryPrice : 1;
      return s + (h.weight_percent / 100) * ratio;
    }, 0);
    return startVal * (growthFactor * investedFrac + cashPct / 100);
  }, [holdings, liveQuotes, portfolio, schwabValue]);
  const hasSchwab = schwabValue != null;

  const totalWeight = holdings.reduce((s, h) => s + (h.weight_percent || 0), 0);
  const isFullyAllocated = Math.abs(totalWeight - 100) < 0.01;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Branded header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-screen-xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold">
                  Client Portal
                </span>
                {approval && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    approval.type === 'approval'
                      ? 'bg-green-50 text-green-700'
                      : 'bg-amber-50 text-amber-700'
                  }`}>
                    {approval.type === 'approval' ? (
                      <><CheckCircle className="w-3 h-3" /> Approved</>
                    ) : (
                      <><AlertTriangle className="w-3 h-3" /> Changes Requested</>
                    )}
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-slate-900">{portfolio?.name || 'Portfolio'}</h1>
              {portfolio?.description && (
                <p className="text-sm text-slate-500 mt-0.5">{portfolio.description}</p>
              )}
            </div>
            <div className="text-right text-sm text-slate-500">
              <p className="font-medium text-slate-700">{user.email}</p>
              {portfolio?.created_at && (
                <p className="text-xs text-slate-400">Portfolio since {new Date(portfolio.created_at).toLocaleDateString()}</p>
              )}
            </div>
          </div>

          {/* Portfolio selector if multiple */}
          {portfolios.length > 1 && (
            <div className="flex gap-2 mt-4">
              {portfolios.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedIdx(i)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedIdx === i
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-4 py-8 space-y-6">
        {/* Allocation wheel — top of client portal */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Allocation Wheel</h2>
          <AllocationPieChart holdings={holdings} schwabPositions={hasSchwab ? schwabData : null} />
          <div className="mt-4 space-y-2">
            {['Core', 'Tilt', 'Satellite'].map((cat) => {
              const COLORS = { Core: 'bg-blue-500', Tilt: 'bg-violet-500', Satellite: 'bg-amber-500' };
              const w = holdings
                .filter((h) => (h.category || 'Core') === cat)
                .reduce((s, h) => s + (h.weight_percent || 0), 0);
              if (w === 0) return null;
              return (
                <div key={cat} className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-16">{cat}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2">
                    <div className={`${COLORS[cat]} h-2 rounded-full`} style={{ width: `${Math.min(w, 100)}%` }} />
                  </div>
                  <span className="text-xs font-mono text-slate-700 w-10 text-right">{w.toFixed(1)}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Performance summary cards */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-slate-400">Performance updates every 5 minutes during market hours</span>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-blue-600 transition-colors"
            title="Refresh performance data"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <p className="text-xs font-medium text-slate-500 mb-1">Year-to-Date</p>
            <p className={`text-xl font-bold ${ytdReturn > 0 ? 'text-green-600' : ytdReturn < 0 ? 'text-red-500' : 'text-slate-500'}`}>
              {ytdReturn != null ? `${ytdReturn > 0 ? '+' : ''}${ytdReturn.toFixed(2)}%` : '--'}
            </p>
            {benchYtd != null && (
              <p className={`text-xs font-medium mt-1 ${benchYtd > 0 ? 'text-green-600' : benchYtd < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                {benchLabel}: {benchYtd > 0 ? '+' : ''}{benchYtd.toFixed(2)}%
              </p>
            )}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <p className="text-xs font-medium text-purple-600 mb-1">Since Creation</p>
            <p className={`text-xl font-bold ${sinceReturn > 0 ? 'text-green-600' : sinceReturn < 0 ? 'text-red-500' : 'text-slate-500'}`}>
              {sinceReturn != null ? `${sinceReturn > 0 ? '+' : ''}${sinceReturn.toFixed(2)}%` : '--'}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <p className="text-xs font-medium text-slate-500 mb-1">1-Year Return</p>
            <p className={`text-xl font-bold ${oneYearReturn > 0 ? 'text-green-600' : oneYearReturn < 0 ? 'text-red-500' : 'text-slate-500'}`}>
              {oneYearReturn != null ? `${oneYearReturn > 0 ? '+' : ''}${oneYearReturn.toFixed(2)}%` : '--'}
            </p>
          </div>
          <div className={`bg-white rounded-xl border shadow-sm p-4 ${hasSchwab ? 'border-emerald-200' : 'border-slate-200'}`}>
            <p className="text-xs font-medium text-slate-500 mb-1">
              {hasSchwab ? 'Account Value' : 'Portfolio Value'}
              {hasSchwab && <span className="ml-1 text-emerald-500">● Schwab</span>}
            </p>
            <p className={`text-xl font-bold ${hasSchwab ? 'text-emerald-700' : 'text-slate-800'}`}>
              {currentPortfolioValue != null ? `$${Math.round(currentPortfolioValue).toLocaleString()}` : '--'}
            </p>
            {portfolio?.starting_value && currentPortfolioValue && currentPortfolioValue !== portfolio.starting_value && (
              <p className={`text-xs mt-1 font-medium ${currentPortfolioValue > portfolio.starting_value ? 'text-green-600' : 'text-red-500'}`}>
                {`${currentPortfolioValue > portfolio.starting_value ? '+' : ''}$${Math.round(currentPortfolioValue - portfolio.starting_value).toLocaleString()} from start`}
              </p>
            )}
            <p className="text-xs text-slate-400 mt-0.5">{holdings.length} holdings</p>
          </div>
        </div>

        {/* Performance chart */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">Portfolio vs Benchmark Performance</h2>
          <PerformanceChart
            holdings={holdings}
            benchmarkTicker={benchmark || null}
            createdAt={portfolio?.created_at}
            cashPercent={portfolio?.cash_percent ?? 0}
            drip={portfolio?.drip_enabled ?? true}
          />
        </div>

        {/* Individual holdings chart */}
        {holdings.length > 1 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">Individual Holdings Performance</h2>
            <HoldingsPerformanceChart holdings={holdings} />
          </div>
        )}

        {/* Holdings table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">
            Your Holdings
            {hasSchwab && <span className="ml-2 text-xs text-emerald-500 font-normal">● Schwab Live Data</span>}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Ticker</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Name</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Role</th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Target %</th>
                  {hasSchwab && <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Actual %</th>}
                  {hasSchwab && <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Value</th>}
                  {hasSchwab && <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2">Drift</th>}
                </tr>
              </thead>
              <tbody>
                {holdings.map((h) => {
                  const sp = hasSchwab ? schwabData?.positions?.find(p => p.ticker === h.ticker) : null;
                  const drift = sp ? sp.actualWeight - h.weight_percent : null;
                  const driftColor = drift != null
                    ? (Math.abs(drift) <= 1 ? 'text-green-600' : Math.abs(drift) <= 3 ? 'text-amber-500' : 'text-red-500')
                    : 'text-slate-300';
                  return (
                    <tr key={h.ticker} className="border-t border-slate-100">
                      <td
                        className="py-2 pr-4 font-mono font-semibold text-blue-600 cursor-pointer hover:text-blue-800 hover:underline"
                        onClick={() => setSummaryTicker(h.ticker)}
                        title="Click for ticker summary"
                      >
                        {h.ticker}
                      </td>
                      <td className="py-2 pr-4 text-slate-600">{h.name}</td>
                      <td className="py-2 pr-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          (h.category || 'Core') === 'Core' ? 'bg-blue-50 text-blue-700'
                            : (h.category || 'Core') === 'Tilt' ? 'bg-violet-50 text-violet-700'
                            : 'bg-amber-50 text-amber-700'
                        }`}>
                          {h.category || 'Core'}
                        </span>
                      </td>
                      <td className="py-2 text-right font-mono text-slate-700 pr-4">{(h.weight_percent || 0).toFixed(1)}%</td>
                      {hasSchwab && (
                        <td className="py-2 text-right font-mono text-emerald-700 pr-4">
                          {sp ? `${sp.actualWeight.toFixed(1)}%` : '—'}
                        </td>
                      )}
                      {hasSchwab && (
                        <td className="py-2 text-right font-mono text-emerald-700 pr-4">
                          {sp ? `$${Math.round(sp.marketValue).toLocaleString()}` : '—'}
                        </td>
                      )}
                      {hasSchwab && (
                        <td className={`py-2 text-right font-mono font-semibold ${driftColor}`}>
                          {drift != null ? `${drift > 0 ? '+' : ''}${drift.toFixed(1)}%` : '—'}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Unmodeled Schwab positions */}
          {hasSchwab && schwabData?.positions && (() => {
            const modelTickers = new Set(holdings.map(h => h.ticker));
            const unmodeled = schwabData.positions.filter(p => !modelTickers.has(p.ticker) && p.marketValue > 0);
            if (!unmodeled.length) return null;
            return (
              <div className="mt-3 p-3 bg-amber-50/60 border border-amber-200 rounded-lg">
                <h4 className="text-xs font-semibold text-amber-700 mb-2">
                  Unmodeled Positions ({unmodeled.length})
                  <span className="ml-1 font-normal text-amber-500">in Schwab but not in target model</span>
                </h4>
                <div className="flex flex-wrap gap-2">
                  {unmodeled.map(p => (
                    <span key={p.ticker} className="inline-flex items-center gap-1 text-xs bg-white px-2 py-1 rounded border border-amber-200 font-mono text-amber-800">
                      {p.ticker}
                      <span className="text-amber-500">{p.actualWeight.toFixed(1)}%</span>
                      <span className="text-amber-400">${Math.round(p.marketValue).toLocaleString()}</span>
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Messages with advisor + approve/comment */}
        {portfolio && (
          <MessagePanel
            portfolioId={portfolio.id}
            userId={user.id}
            userEmail={user.email}
            userName={userName}
            userRole="client"
            showApprovalActions={true}
          />
        )}

        <p className="text-center text-xs text-slate-400 pb-4">
          Powered by <span className="font-semibold text-blue-600">WeightWatch Portfolios</span>
        </p>
      </main>

      {/* Ticker summary modal */}
      {summaryTicker && (
        <TickerSummaryModal ticker={summaryTicker} onClose={() => setSummaryTicker(null)} />
      )}
    </div>
  );
}
